import useMutation from '@hooks/useGQLMutation'
import {
  InitiateCallingSessionDocument,
  type InitiateCallingSessionMutation,
  type InitiateCallingSessionMutationVariables,
} from '@frontend/graphql/mutations/initiateCallingSession.generated'
import { useAlertsStore } from '@stores/alertsStore'
import { toast } from '@stores/toastStore'
import { FormDialog } from '@components/molecules/FormDialog'
import { scopedTranslation } from '@frontend/utils/I18n'
import { useEffect } from 'react'
import type { InitiateCallingSessionDialogState } from '../../../supporters-management/types/dialogTypes'

const t = scopedTranslation('components.initiate_calling_session_dialog')

const ALERTS_PATH = 'initiateCallingSession' as const

type InitiateCallingSessionDialogProps = {
  onClose: () => void
  dialogState: InitiateCallingSessionDialogState
}

export function InitiateCallingSessionDialog({ onClose, dialogState }: InitiateCallingSessionDialogProps) {
  const { callingSessionId, isPaused = false, onCallingSessionStarted } = dialogState.dialogData
  const { setAlerts, clearAlerts } = useAlertsStore(({ setAlerts, clearAlerts }) => ({
    setAlerts,
    clearAlerts,
  }))

  useEffect(() => {
    setAlerts({
      alertsPath: ALERTS_PATH,
      alerts: [
        {
          type: 'info',
          details: {
            title: t('alerts.info.title'),
          },
        },
      ],
    })
  }, [])

  const initiateCallingSessionMutationHook = useMutation(InitiateCallingSessionDocument)

  const onSubmit = () => {
    clearAlerts(ALERTS_PATH)

    const mutationData: InitiateCallingSessionMutationVariables = {
      callingSessionId,
    }

    initiateCallingSessionMutationHook.mutate(mutationData, {
      onSuccess: (response: InitiateCallingSessionMutation) => {
        if (response?.initiateCallingSession?.success) {
          clearAlerts(ALERTS_PATH)
          onClose()
          toast.success(isPaused ? t('alerts.success.title_resume') : t('alerts.success.title'))
          onCallingSessionStarted?.()
        } else {
          setAlerts({
            alertsPath: ALERTS_PATH,
            alerts:
              response?.initiateCallingSession?.errors?.map((error) => ({
                type: 'error' as const,
                details: {
                  title: error.message,
                },
              })) ?? [],
          })
        }
      },
      onError: () => {
        setAlerts({
          alertsPath: ALERTS_PATH,
          alerts: [
            {
              type: 'error',
              details: {
                title: t('errors.initiate_calling_session_failed'),
              },
            },
          ],
        })
      },
    })
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      clearAlerts(ALERTS_PATH)
      onClose()
    }
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    onSubmit()
  }

  return (
    <FormDialog.Root
      open={true}
      onOpenChange={handleOpenChange}
      testId="initiate-calling-session-dialog"
      preventOutsideClick
    >
      <FormDialog.Title alertsPath={ALERTS_PATH}>{isPaused ? t('title_resume') : t('title')}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit}>
        <FormDialog.Body>
          <p>{isPaused ? t('body_resume') : t('body')}</p>
        </FormDialog.Body>
        <FormDialog.Footer
          submitText={isPaused ? t('form.resume_session') : t('form.start_session')}
          isLoading={initiateCallingSessionMutationHook.isLoading}
        />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
